import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Paper, CircularProgress, Chip,
  Select, MenuItem, FormControl, IconButton,
} from "@mui/material";
import {
  ArrowBack, NavigateBefore, NavigateNext,
  CheckCircle, Cancel, AccessTime,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetMyAttendance }  from "../../../../Services/Services";

function EmployeeMyAttendance() {
  const navigate = useNavigate();
  const empProfileId = localStorage.getItem("emp_profile_id") || "";
  const empName = localStorage.getItem("emp_name") || "Employee";
  const empUniqueId = localStorage.getItem("emp_unique_id") || "";

  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState("month");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    if (localStorage.getItem("emp_logged_in") !== "true") {
      navigate("/employee-login");
    }
  }, []);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        profile_id: empProfileId,
        filter: filter,
      });
      const data = await GetMyAttendance(params.toString());
      const recordsData = Array.isArray(data) ? data : (data?.results || []);
      setRecords(recordsData);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load attendance");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filter, empProfileId]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const totalPages = Math.ceil(records.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRecords = records.slice(startIndex, startIndex + itemsPerPage);

  const presentCount = records.filter(r => r.status === "Present").length;
  const absentCount = records.filter(r => r.status === "Absent").length;

  const totalHours = records.reduce((sum, r) => {
    if (r.total_time && r.total_time !== "--") {
      const match = r.total_time.match(/(\d+)h\s*(\d+)m/);
      if (match) return sum + parseInt(match[1]) + parseInt(match[2]) / 60;
    }
    return sum;
  }, 0);

  return (
    <>
      <ToastContainer />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa", p: 2 }}>

        {/* Header */}
        <Paper sx={{ p: 1.5, borderRadius: "12px", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => navigate("/employee-dashboard")} sx={{ color: "#64748b" }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>📋 My Attendance</Typography>
            <Typography sx={{ fontSize: "11px", color: "#64748b" }}>{empName} • {empUniqueId}</Typography>
          </Box>
        </Paper>

        {/* Stats */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 2 }}>
          <Paper sx={{ p: 1.5, textAlign: "center", borderRadius: "10px", bgcolor: "#f0fdf4" }}>
            <CheckCircle sx={{ fontSize: 20, color: "#16a34a" }} />
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#16a34a", mt: 0.5 }}>{presentCount}</Typography>
            <Typography sx={{ fontSize: "10px", color: "#64748b" }}>Present</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, textAlign: "center", borderRadius: "10px", bgcolor: "#fef2f2" }}>
            <Cancel sx={{ fontSize: 20, color: "#dc2626" }} />
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#dc2626", mt: 0.5 }}>{absentCount}</Typography>
            <Typography sx={{ fontSize: "10px", color: "#64748b" }}>Absent</Typography>
          </Paper>
          <Paper sx={{ p: 1.5, textAlign: "center", borderRadius: "10px", bgcolor: "#eff6ff" }}>
            <AccessTime sx={{ fontSize: 20, color: "#3b82f6" }} />
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#3b82f6", mt: 0.5 }}>{totalHours.toFixed(1)}h</Typography>
            <Typography sx={{ fontSize: "10px", color: "#64748b" }}>Total</Typography>
          </Paper>
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 1.5, borderRadius: "10px", mb: 2 }}>
          <FormControl size="small" fullWidth>
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ fontSize: "13px" }}>
              <MenuItem value="today">📅 Today</MenuItem>
              <MenuItem value="week">🗓️ This Week</MenuItem>
              <MenuItem value="month">📆 This Month</MenuItem>
              <MenuItem value="year">📊 This Year</MenuItem>
              <MenuItem value="all">🗂️ All Time</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {/* Records */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress sx={{ color: "#ff8800" }} /></Box>
        ) : paginatedRecords.length > 0 ? (
          <Box>
            {paginatedRecords.map((r, i) => {
              const isPresent = r.status === "Present";
              const isAuto = r.auto_marked;
              const dateObj = typeof r.date === 'string' ? new Date(r.date) : r.date;
              const dayName = dateObj.toLocaleDateString("en-IN", { weekday: "short" });
              const dateStr = dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

              return (
                <Paper key={i}
                  sx={{
                    p: 2, mb: 1, borderRadius: "12px",
                    borderLeft: `4px solid ${isPresent ? "#16a34a" : "#dc2626"}`,
                    display: "flex", alignItems: "center", gap: 2
                  }}>
                  {/* Date Box */}
                  <Box sx={{
                    textAlign: "center", bgcolor: isPresent ? "#f0fdf4" : "#fef2f2",
                    borderRadius: "8px", p: 1, minWidth: 55
                  }}>
                    <Typography sx={{
                      fontSize: "10px", fontWeight: 600,
                      color: isPresent ? "#16a34a" : "#dc2626",
                      textTransform: "uppercase"
                    }}>
                      {dayName}
                    </Typography>
                    <Typography sx={{
                      fontSize: "16px", fontWeight: 700,
                      color: isPresent ? "#15803d" : "#b91c1c",
                      lineHeight: 1.1
                    }}>
                      {dateStr.split(" ")[0]}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "#64748b" }}>{dateStr.split(" ")[1]}</Typography>
                  </Box>

                  {/* Details */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
                      <Chip label={r.status} size="small"
                        sx={{
                          bgcolor: isPresent ? "#dcfce7" : "#fee2e2",
                          color: isPresent ? "#16a34a" : "#dc2626",
                          fontWeight: 600, fontSize: "10px", height: 20
                        }} />
                      {isAuto && !isPresent && (
                        <Chip label="Auto" size="small"
                          sx={{ bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600, fontSize: "9px", height: 18 }} />
                      )}
                    </Box>

                    {isPresent ? (
                      <>
                        <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                          In: <strong style={{ color: "#16a34a" }}>{r.in_time || "--"}</strong>
                          {r.out_time && r.out_time !== "--" && (
                            <> | Out: <strong style={{ color: "#dc2626" }}>{r.out_time}</strong></>
                          )}
                        </Typography>
                        {r.total_time && r.total_time !== "--" && (
                          <Typography sx={{ fontSize: "11px", color: "#64748b", mt: 0.25 }}>
                            ⏱️ {r.total_time}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                        {isAuto ? "Not marked (auto absent)" : "Marked absent"}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}>
            <Typography sx={{ fontSize: "48px" }}>📭</Typography>
            <Typography sx={{ color: "#64748b", mt: 1 }}>No attendance records</Typography>
            <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.5 }}>Try changing the filter above</Typography>
          </Paper>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{
            display: "flex", justifyContent: "center", gap: 2, mt: 2,
            alignItems: "center", bgcolor: "#fff", p: 1, borderRadius: "10px"
          }}>
            <Button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              startIcon={<NavigateBefore />}
              sx={{ textTransform: "none", color: "#ff8800", "&.Mui-disabled": { color: "#cbd5e1" } }}
            >
              Prev
            </Button>
            <Typography sx={{ fontWeight: 600, color: "#333", fontSize: "13px" }}>
              {currentPage} / {totalPages}
            </Typography>
            <Button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              endIcon={<NavigateNext />}
              sx={{ textTransform: "none", color: "#ff8800", "&.Mui-disabled": { color: "#cbd5e1" } }}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
}

export default EmployeeMyAttendance;